// src/components/TextCompare/SidebarTools.tsx

import React, { useState } from 'react';
import {
    Settings,
    Trash2,
    CaseLower,
    SortAsc,
    Scissors,
    Zap,
    Menu,
    X,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { ComparisonOptions } from '../../services/apiService';
import { TransformType } from '../../hooks/useTextComparison';

interface SidebarToolsProps {
    options: ComparisonOptions;
    updateOptions: (opts: Partial<ComparisonOptions>) => void;
    onTransformText: (type: TransformType) => void;
    isProcessing: boolean;
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
    activeTransform: Set<TransformType>;
}

const SidebarTools: React.FC<SidebarToolsProps> = ({
    options,
    updateOptions,
    onTransformText,
    isProcessing,
    isCollapsed,
    setIsCollapsed,
    activeTransform
}) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleSidebar = () => setIsCollapsed(!isCollapsed);
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    return (
        <>
            {/* Mobile Toggle Button */}
            <button
                className={`!hidden max-md:!flex fixed bottom-6 right-6 bg-[#1a73e8] text-white p-3 rounded-full shadow-[0_4px_12px_rgba(26,115,232,0.4)] z-[101] border-none cursor-pointer`}
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "Close settings" : "Open settings"}
            >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            <aside className={`w-[220px] bg-white border-r border-[#f1f5f9] sticky top-[80px] rounded-xl h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden py-4 px-0 flex flex-col transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] z-[100] overscroll-y-contain dark:bg-slate-800 dark:border-slate-700
                ${isCollapsed ? '!w-0 !border-r-transparent !px-0 !overflow-visible' : ''} 
                max-md:fixed max-md:-left-full max-md:top-0 max-md:bottom-0 max-md:w-[280px] max-md:h-screen max-md:transition-[left] max-md:duration-300 max-md:ease-out max-md:rounded-none max-md:border-none
                ${isMobileMenuOpen ? 'max-md:left-0' : ''}
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 dark:hover:scrollbar-thumb-slate-500`}
            >
                <button
                    className={`absolute right-0 top-2 w-6 h-6 bg-white border border-[#eef2f6] rounded-full flex items-center justify-center cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.04)] z-10 text-slate-400 transition-all duration-300 p-0 hover:text-[#1a73e8] hover:border-[#d2e3fc] hover:bg-[#f8faff] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:border-slate-500
                        ${isCollapsed ? '!right-[-1px] !w-11 !h-7 text-[#1a73e8] bg-white !border-white rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:bg-slate-800 dark:!border-slate-800 dark:text-blue-400' : ''}`}
                    onClick={toggleSidebar}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    title={isCollapsed ? "Show Settings" : "Hide Settings"}
                >
                    {isCollapsed ? (
                        <>
                            <Settings size={14} className="mr-0.5 shrink-0 animate-[rotate-once_0.5s_ease-out]" />
                            <ChevronRight size={12} className="shrink-0" />
                        </>
                    ) : (
                        <ChevronLeft size={16} className="shrink-0" />
                    )}
                </button>

                <div className={`flex-1 flex flex-col transition-opacity duration-200 w-[220px] max-md:w-full ${isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    <div className="px-3 mb-8 last:mb-0">
                        <h3 className="flex items-center gap-3 text-[0.7rem] font-bold text-slate-400 uppercase tracking-widest mb-5 dark:text-slate-500">
                            <Settings size={16} />
                            <span>SETTINGS</span>
                        </h3>
                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Live Sync</span>
                            </div>
                            <label className="relative inline-block w-8 h-[18px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={options.realTime}
                                    onChange={(e) => updateOptions({ realTime: e.target.checked })}
                                    aria-label="Toggle real-time comparison"
                                />
                                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-slate-200 transition-colors duration-400 rounded-full before:absolute before:content-[''] before:h-3.5 before:w-3.5 before:left-0.5 before:bottom-[2px] before:bg-white before:transition-transform before:duration-400 before:rounded-full before:shadow-[0_1px_3px_rgba(0,0,0,0.2)] peer-checked:bg-[#1a73e8] peer-checked:before:translate-x-[14px] dark:bg-slate-600 dark:before:bg-slate-200 dark:peer-checked:bg-blue-600"></span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Unified View</span>
                            </div>
                            <label className="relative inline-block w-8 h-[18px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={options.viewType === 'unified'}
                                    onChange={(e) => updateOptions({ viewType: e.target.checked ? 'unified' : 'split' })}
                                    aria-label="Toggle unified view"
                                />
                                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-slate-200 transition-colors duration-400 rounded-full before:absolute before:content-[''] before:h-3.5 before:w-3.5 before:left-0.5 before:bottom-[2px] before:bg-white before:transition-transform before:duration-400 before:rounded-full before:shadow-[0_1px_3px_rgba(0,0,0,0.2)] peer-checked:bg-[#1a73e8] peer-checked:before:translate-x-[14px] dark:bg-slate-600 dark:before:bg-slate-200 dark:peer-checked:bg-blue-600"></span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Ignore Case</span>
                            </div>
                            <label className="relative inline-block w-8 h-[18px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={options.ignoreCase}
                                    onChange={(e) => updateOptions({ ignoreCase: e.target.checked })}
                                    aria-label="Ignore case sensitivity"
                                />
                                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-slate-200 transition-colors duration-400 rounded-full before:absolute before:content-[''] before:h-3.5 before:w-3.5 before:left-0.5 before:bottom-[2px] before:bg-white before:transition-transform before:duration-400 before:rounded-full before:shadow-[0_1px_3px_rgba(0,0,0,0.2)] peer-checked:bg-[#1a73e8] peer-checked:before:translate-x-[14px] dark:bg-slate-600 dark:before:bg-slate-200 dark:peer-checked:bg-blue-600"></span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Ignore Spaces</span>
                            </div>
                            <label className="relative inline-block w-8 h-[18px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={options.ignoreWhitespace}
                                    onChange={(e) => updateOptions({ ignoreWhitespace: e.target.checked })}
                                    aria-label="Ignore whitespace differences"
                                />
                                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-slate-200 transition-colors duration-400 rounded-full before:absolute before:content-[''] before:h-3.5 before:w-3.5 before:left-0.5 before:bottom-[2px] before:bg-white before:transition-transform before:duration-400 before:rounded-full before:shadow-[0_1px_3px_rgba(0,0,0,0.2)] peer-checked:bg-[#1a73e8] peer-checked:before:translate-x-[14px] dark:bg-slate-600 dark:before:bg-slate-200 dark:peer-checked:bg-blue-600"></span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Ignore Symbols</span>
                            </div>
                            <label className="relative inline-block w-8 h-[18px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={options.ignorePunctuation}
                                    onChange={(e) => updateOptions({ ignorePunctuation: e.target.checked })}
                                    aria-label="Ignore punctuation differences"
                                />
                                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-slate-200 transition-colors duration-400 rounded-full before:absolute before:content-[''] before:h-3.5 before:w-3.5 before:left-0.5 before:bottom-[2px] before:bg-white before:transition-transform before:duration-400 before:rounded-full before:shadow-[0_1px_3px_rgba(0,0,0,0.2)] peer-checked:bg-[#1a73e8] peer-checked:before:translate-x-[14px] dark:bg-slate-600 dark:before:bg-slate-200 dark:peer-checked:bg-blue-600"></span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center mb-3 py-1">
                            <div className="flex-1 overflow-hidden pr-2">
                                <span className="text-[0.875rem] font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-300">Precision</span>
                            </div>
                            <div className="flex bg-slate-100 p-0.5 rounded-md gap-0.5 dark:bg-slate-700">
                                <button
                                    className={`flex-1 px-2 py-1 border-none bg-transparent text-xs font-semibold text-slate-500 rounded cursor-pointer transition-all duration-200 dark:text-slate-400 ${!options.characterLevel ? 'bg-white text-[#1a73e8] shadow-[0_1px_2px_rgba(60,64,67,0.3)] dark:bg-slate-600 dark:text-blue-400' : 'hover:bg-slate-200 dark:hover:bg-slate-600'}`}
                                    onClick={() => updateOptions({ characterLevel: false })}
                                    aria-label="Set word-level difference mode"
                                >
                                    Word
                                </button>
                                <button
                                    className={`flex-1 px-2 py-1 border-none bg-transparent text-xs font-semibold text-slate-500 rounded cursor-pointer transition-all duration-200 dark:text-slate-400 ${options.characterLevel ? 'bg-white text-[#1a73e8] shadow-[0_1px_2px_rgba(60,64,67,0.3)] dark:bg-slate-600 dark:text-blue-400' : 'hover:bg-slate-200 dark:hover:bg-slate-600'}`}
                                    onClick={() => updateOptions({ characterLevel: true })}
                                    aria-label="Set character-level difference mode"
                                >
                                    Char
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="px-3 mb-8 last:mb-0 mt-2">
                        <h3 className="flex items-center gap-3 text-[0.7rem] font-bold text-slate-400 uppercase tracking-widest mb-5 dark:text-slate-500">
                            <Zap size={16} />
                            <span>QUICK TOOLS</span>
                        </h3>

                        <div className="grid grid-cols-2 gap-2">
                            <button
                                className={`flex flex-col items-center py-2.5 px-1 bg-slate-50 border border-transparent rounded-md text-slate-600 cursor-pointer transition-all duration-200 gap-1.5 hover:not(:disabled):bg-slate-100 hover:not(:disabled):text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-400 dark:hover:not(:disabled):bg-slate-600 dark:hover:not(:disabled):text-slate-200 active:not(:disabled):bg-slate-200 dark:active:not(:disabled):bg-slate-500 ${activeTransform?.has('lowercase') ? '!bg-blue-50 !text-[#1967d2] !border-[#d2e3fc] dark:!bg-blue-900/30 dark:!text-blue-400 dark:!border-blue-800' : ''}`}
                                onClick={() => onTransformText('lowercase')}
                                disabled={isProcessing}
                                title="Convert to lowercase"
                                aria-label="Convert all text to lowercase"
                            >
                                <CaseLower size={18} />
                                <span className="text-[0.7rem] font-medium leading-[1]">To lower</span>
                            </button>

                            <button
                                className={`flex flex-col items-center py-2.5 px-1 bg-slate-50 border border-transparent rounded-md text-slate-600 cursor-pointer transition-all duration-200 gap-1.5 hover:not(:disabled):bg-slate-100 hover:not(:disabled):text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-400 dark:hover:not(:disabled):bg-slate-600 dark:hover:not(:disabled):text-slate-200 active:not(:disabled):bg-slate-200 dark:active:not(:disabled):bg-slate-500 ${activeTransform?.has('sort') ? '!bg-blue-50 !text-[#1967d2] !border-[#d2e3fc] dark:!bg-blue-900/30 dark:!text-blue-400 dark:!border-blue-800' : ''}`}
                                onClick={() => onTransformText('sort')}
                                disabled={isProcessing}
                                title="Sort lines alphabetically"
                                aria-label="Sort all lines alphabetically"
                            >
                                <SortAsc size={18} />
                                <span className="text-[0.7rem] font-medium leading-[1]">Sort lines</span>
                            </button>

                            <button
                                className={`flex flex-col items-center py-2.5 px-1 bg-slate-50 border border-transparent rounded-md text-slate-600 cursor-pointer transition-all duration-200 gap-1.5 hover:not(:disabled):bg-slate-100 hover:not(:disabled):text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-400 dark:hover:not(:disabled):bg-slate-600 dark:hover:not(:disabled):text-slate-200 active:not(:disabled):bg-slate-200 dark:active:not(:disabled):bg-slate-500 ${activeTransform?.has('trim') ? '!bg-blue-50 !text-[#1967d2] !border-[#d2e3fc] dark:!bg-blue-900/30 dark:!text-blue-400 dark:!border-blue-800' : ''}`}
                                onClick={() => onTransformText('trim')}
                                disabled={isProcessing}
                                title="Remove leading/trailing whitespace"
                                aria-label="Trim whitespace from lines"
                            >
                                <Scissors size={18} />
                                <span className="text-[0.7rem] font-medium leading-[1]">Trim</span>
                            </button>

                            <button
                                className={`flex flex-col items-center py-2.5 px-1 bg-slate-50 border border-transparent rounded-md text-slate-600 cursor-pointer transition-all duration-200 gap-1.5 hover:not(:disabled):bg-slate-100 hover:not(:disabled):text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-400 dark:hover:not(:disabled):bg-slate-600 dark:hover:not(:disabled):text-slate-200 active:not(:disabled):bg-slate-200 dark:active:not(:disabled):bg-slate-500 ${activeTransform?.has('clean') ? '!bg-blue-50 !text-[#1967d2] !border-[#d2e3fc] dark:!bg-blue-900/30 dark:!text-blue-400 dark:!border-blue-800' : ''}`}
                                onClick={() => onTransformText('clean')}
                                disabled={isProcessing}
                                title="Remove empty lines"
                                aria-label="Remove all empty lines"
                            >
                                <Trash2 size={18} />
                                <span className="text-[0.7rem] font-medium leading-[1]">Clean</span>
                            </button>
                        </div>
                    </div>

                </div>
            </aside>

            {/* Overlay for mobile */}
            {isMobileMenuOpen && (
                <div className="sidebar-overlay" onClick={() => setIsMobileMenuOpen(false)} />
            )}
        </>
    );
};

export default SidebarTools;
